/* eslint-disable jsx-a11y/control-has-associated-label, @typescript-eslint/ban-ts-comment, @typescript-eslint/no-unsafe-member-access, react-hooks/exhaustive-deps */
import cn from 'classnames';
import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import type { Dispatch, SetStateAction, RefObject } from 'react';
import type ReactPlayer from 'react-player/youtube';

import type { Locale } from '@shared/libs';
import { Tab } from '@shared/ui';

import type { TVideo } from '../../../model';
import styles from '../../ai-player.module.css';
import { All } from './all';
import { Diagramm } from './diagramm';

type TProps = {
  locale: Locale;
  chords: TVideo['chords'];
  mp3?: string;
  setIsPlay: Dispatch<SetStateAction<boolean>>;
  isPlay: boolean;
  startMs: number | null;
  onChange: (val: number) => void;
  refPlayer: RefObject<ReactPlayer>;
};

function binSearch(arr: Array<string>, toFind: number) {
  if (!arr) return -1;
  let first = 0;
  let last = arr.length - 1;

  while (first < last) {
    const mid = first + Math.floor((last - first) / 2);
    if (Number(arr[mid]) >= toFind) {
      last = mid;
    } else {
      first = mid + 1;
    }
  }
  if (Number(arr[last]) === toFind || last === 0 || last === arr.length - 1) {
    return last;
  }

  return last - 1;
}

type TChord = {
  position: number;
  chord: string;
  start: number;
  end: number;
  next: string;
};

export const Player = ({
  locale,
  chords,
  mp3,
  setIsPlay,
  isPlay,
  startMs,
  onChange,
  refPlayer,
}: TProps) => {
  const timeoutId = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [tabMode, setTabMode] = useState<'diagramm' | 'all'>('diagramm');

  const [activeChord, setActiveChord] = useState<TChord | null>(null);

  const mapChord = useMemo<Record<number, { position: number; chord: string }>>(() => {
    if (!chords) {
      return {};
    }
    return chords.reduce<Record<string, TChord>>(
      (acc, { chord, position, start, end }) => ({
        ...acc,
        [start]: {
          chord,
          position,
          end: Number(end),
          start: Number(start),
          next: end.toString(),
        },
      }),
      {},
    );
  }, [chords]);

  const mapChordIndexSort = useMemo<Array<string>>(() => {
    return Object.keys(mapChord).sort((a, b) => (Number(a) > Number(b) ? 1 : -1));
  }, [mapChord]);

  useEffect(() => {
    // @ts-expect-error
    if (isPlay && refPlayer.current?.player?.isPlaying) {
      if (activeChord) {
        const { next, end, start } = activeChord;

        timeoutId.current = setTimeout(
          () => {
            const nextChord = mapChord[next] as TChord | null;
            if (nextChord) {
              // @ts-expect-error
              if (refPlayer.current?.player?.isPlaying) {
                setActiveChord(nextChord);
              }
            }
          },
          (end - start) * 1000,
        );
      } else {
        if (timeoutId.current) {
          clearTimeout(timeoutId.current);
        }
        setActiveChord(null);
      }
    }

    return () => {
      if (timeoutId.current) {
        clearTimeout(timeoutId.current);
      }
    };
    // @ts-expect-error
  }, [activeChord, mapChord, isPlay, refPlayer.current?.player?.isPlaying]);

  useEffect(() => {
    // @ts-expect-error
    if (refPlayer.current?.player?.isPlaying === false || !isPlay) {
      if (timeoutId.current) {
        clearTimeout(timeoutId.current);
      }
    }
    // @ts-expect-error
  }, [refPlayer.current?.player?.isPlaying, isPlay]);

  useEffect(() => {
    if (startMs !== null) {
      const index = binSearch(mapChordIndexSort, startMs);

      if (index >= 0) {
        const start = mapChordIndexSort[index];
        const item = mapChord[start] as TChord | undefined;
        if (item) {
          // @ts-expect-error
          if (refPlayer.current?.player?.player?.props?.playing) {
            setActiveChord(item);
          }
        } else {
          setActiveChord(null);
        }
      } else {
        setActiveChord(null);
      }
    } else {
      setActiveChord(null);
    }
  }, [mapChord, mapChordIndexSort, startMs]);

  const togglePlay = useCallback(() => {
    setIsPlay(!isPlay);
  }, [isPlay, setIsPlay]);

  useEffect(() => {
    setIsPlay(false);
  }, [mp3, setIsPlay]);

  if (!chords) {
    return null;
  }

  return (
    <>
      <div className={styles.tabs}>
        <Tab filled={tabMode === 'diagramm'} onClick={() => setTabMode('diagramm')}>
          <Tab.Title>{locale.AI_PLAYER_DIAGRAM || 'AI_PLAYER_DIAGRAM'}</Tab.Title>
        </Tab>
        <Tab filled={tabMode === 'all'} onClick={() => setTabMode('all')}>
          <Tab.Title>{locale.AI_PLAYER_CHORDS || 'AI_PLAYER_CHORDS'}</Tab.Title>
        </Tab>
      </div>

      <div className={styles.controllGroup}>
        <div>
          <button
            type="button"
            className={cn(styles.btn, styles.back)}
            onClick={() => {
              onChange(0);
              setIsPlay(false);
            }}
          />
          <button
            type="button"
            className={cn(styles.btn, {
              [styles.play]: !isPlay,
              [styles.pause]: isPlay,
            })}
            onClick={togglePlay}
          />
        </div>

        <div>{false && <button type="button" className={cn(styles.btn, styles.print)} />}</div>
      </div>
      {tabMode === 'diagramm' ? (
        <All chords={chords} activePosition={activeChord?.position} onChange={onChange} />
      ) : (
        <Diagramm chords={chords} activePosition={activeChord?.position} onChange={onChange} />
      )}
    </>
  );
};
